import React, { useState, useRef } from "react";
import axios from "axios";
import { Loader2, Upload, X, ImageIcon } from "lucide-react";
import { Button } from "../ui/button";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Inline product photo picker.
 *
 * Props:
 *   itemId     — inventory item id (required; photos attach to an existing item)
 *   photoUrl   — current full photo URL (optional)
 *   onChange   — (patch) => void, called with { photo_url, photo_thumbnail_url } or nulls on remove
 */
export default function ProductPhotoUpload({ itemId, photoUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const token = () => localStorage.getItem("token");

  const upload = async (file) => {
    if (!file) return;
    if (!itemId) {
      toast.error("Save the product first, then add a photo");
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)) {
      toast.error("Use JPG, PNG, or WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/inventory/items/${itemId}/photo`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token()}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      onChange?.({
        photo_url: data.photo_url,
        photo_thumbnail_url: data.photo_thumbnail_url,
      });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!itemId) return;
    setUploading(true);
    try {
      await axios.delete(`${API}/inventory/items/${itemId}/photo`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      onChange?.({ photo_url: null, photo_thumbnail_url: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const resolvedUrl = photoUrl
    ? photoUrl.startsWith("http")
      ? photoUrl
      : `${API.replace(/\/api$/, "")}${photoUrl}`
    : null;

  return (
    <div className="space-y-2" data-testid="product-photo-upload">
      <label className="text-xs font-medium text-muted-foreground">Photo (optional)</label>
      <div className="flex items-center gap-3">
        {resolvedUrl ? (
          <div className="relative h-20 w-20 rounded-lg overflow-hidden border border-border bg-muted/30">
            <img
              src={resolvedUrl}
              alt="Product"
              className="h-full w-full object-cover"
              data-testid="product-photo-preview"
            />
            <button
              type="button"
              onClick={remove}
              disabled={uploading}
              className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 disabled:opacity-50"
              aria-label="Remove photo"
              data-testid="product-photo-remove"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            disabled={uploading || !itemId}
            className={`h-20 w-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
            } ${!itemId ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="product-photo-dropzone"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-4 h-4 mb-1" />
                <span className="text-[10px]">Add photo</span>
              </>
            )}
          </button>
        )}

        <div className="flex-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !itemId}
            data-testid="product-photo-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {resolvedUrl ? "Replace" : "Upload photo"}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1">
            {itemId ? "JPG, PNG, or WebP · max 5 MB" : "Save the product first"}
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
            e.target.value = "";
          }}
          data-testid="product-photo-input"
        />
      </div>
    </div>
  );
}
